import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Bell, CheckCheck } from "lucide-react";

export function NotificationsBell({ prefix = "" }) {
  const [data, setData] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const markAll = async () => { try { await api.post("/notifications/read-all"); load(); } catch { toast.error("Impossibile aggiornare le notifiche"); } };
  const openTicketNotification = async (n) => {
    setOpen(false);
    if (n.kind === "ticket" && !n.ticket_id) toast.info("Questa notifica precedente non ha un ticket collegato: selezionalo dall'elenco.");
    if (n.read) return;
    try {
      await api.post(`/notifications/${n.id}/read`);
      await load();
    } catch { toast.error("Non è stato possibile segnare la notifica come letta."); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-lg hover:bg-secondary text-muted-foreground transition-colors" data-testid={`${prefix}notifications-bell`} aria-label="Comunicazioni">
          <Bell className="w-5 h-5" />
          {data.unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center" data-testid={`${prefix}notifications-count`}>{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" collisionPadding={12} className="w-80 max-w-[calc(100vw-24px)] p-0" data-testid={`${prefix}notifications-panel`}>
        <div className="flex flex-wrap gap-2 items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-head font-semibold text-sm">Comunicazioni</span>
          {data.unread > 0 && <button onClick={markAll} className="app-text-button text-xs text-primary inline-flex items-center gap-1" data-testid={`${prefix}notifications-readall`}><CheckCheck className="w-3.5 h-3.5" /> Segna lette</button>}
        </div>
        <div className="max-h-[min(60dvh,24rem)] overflow-y-auto">
          {data.items.length === 0 && <p className="text-sm text-muted-foreground text-center py-8" data-testid={`${prefix}notifications-empty`}>Nessuna comunicazione.</p>}
          {data.items.map((n) => {
            const isTicket = n.kind === "ticket";
            const isOffer = n.kind === "energy_offer" && n.offer_code;
            const isCommission = n.kind === "energy_commission_monitor";
            const isMasterNetwork = n.kind === "master_threshold";
            const isReturn = n.kind === "shipment_return";
            const linked = isTicket || isOffer || isCommission || isMasterNetwork || isReturn;
            const Row = linked ? Link : "div";
            const to = isReturn ? "/app/console-spedizioni" : isMasterNetwork ? "/app/dealers" : isCommission ? "/app/dashboard#commission-monitor" : isOffer ? `/app/provvigioni-offerte-energia?offer=${encodeURIComponent(n.offer_code)}` : n.ticket_id ? `/app/assistenza?ticket=${encodeURIComponent(n.ticket_id)}` : "/app/assistenza";
            return (
              <Row key={n.id} {...(linked ? { to, onClick: () => openTicketNotification(n) } : {})}
                className={`block px-4 py-3 border-b border-border/60 ${n.read ? "" : "bg-primary/5"} ${linked ? "hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary transition-colors" : ""}`}
                data-testid={`${prefix}notification-${n.id}`}>
                <div className="flex items-center gap-2">
                  {!n.read && <span className="w-2 h-2 rounded-full bg-primary shrink-0" />}
                  <p className="text-sm font-medium" data-testid={`${prefix}notification-title-${n.id}`}>{n.title}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 break-words" data-testid={`${prefix}notification-body-${n.id}`}>{n.body}</p>
                <p className="text-[10px] text-muted-foreground/70 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString("it-IT") : ""}</p>
                {linked && <span className="inline-block mt-1 text-xs font-medium text-primary" data-testid={`${prefix}notification-open-${n.id}`}>{isReturn ? "Apri console spedizioni" : isMasterNetwork ? "Valuta rete Master" : isCommission ? "Apri aggiornamento provvigioni" : isOffer ? "Apri offerta" : n.ticket_id ? "Apri ticket" : "Apri assistenza"}</span>}
              </Row>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
